import React, { useEffect, useState, useMemo } from 'react';
import { Question } from '@/modules/Questionnaire/components/Question';
import { Container, TrashIcon, TextTooltip, Text } from '@/ui';
import { Button } from '@/components';
import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useFieldArray, useWatch } from 'react-hook-form';

const createLinkFromText = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) =>
    urlRegex.test(part) ? (
      <a href={part} key={index} target='_blank' rel='noopener noreferrer'>
        {part}
      </a>
    ) : (
      <span key={index}>{part}</span>
    )
  );
};

export const QuestionGroup = ({
  questionGroup,
  watch,
  preFilledQuestions,
  control,
  isSelectScoringTooltip,
  ...props
}) => {
  const [visible, setVisible] = useState(true);
  const watchedFields = useWatch({
    control,
    name: questionGroup?.visibleConditions
      ? questionGroup?.visibleConditions?.map((item) => item.id)
      : []
  });

  //isVisible logic
  useEffect(() => {
    const conditions = questionGroup.visibleConditions;
    const operator = questionGroup.visibleOperator;

    if (conditions && Array.from(conditions).length > 0) {
      let isVisible = visible;

      if (operator === 'AND') {
        isVisible = true;
        for (const [index, value] of watchedFields.entries()) {
          if (
            Array.isArray(value)
              ? !value.includes(conditions[index].value)
              : conditions[index].value !== value
          ) {
            isVisible = false;
            break;
          }
        }
      }

      if (operator === 'OR') {
        isVisible = false;
        for (const [index, value] of watchedFields.entries()) {
          if (
            Array.isArray(value)
              ? value.includes(conditions[index].value)
              : conditions[index].value === value
          ) {
            isVisible = true;
            break;
          }
        }
      }

      if (operator === 'NOT') {
        isVisible = true;
        for (const [index, value] of watchedFields.entries()) {
          if (
            Array.isArray(value)
              ? value.includes(conditions[index].value)
              : conditions[index].value === value
          ) {
            isVisible = false;
            break;
          }
        }
      }

      if (visible !== isVisible) setVisible(isVisible);
    }
  }, [watchedFields, questionGroup]);

  if (visible) {
    if (!questionGroup.isMultiple) {
      return (
        <QuestionGroupWrapper>
          {questionGroup.title && (
            <Container $marginBottom={20}>
              <Text $fontSize={17} $fontWeight={600}>
                {createLinkFromText(questionGroup.title)}
              </Text>
            </Container>
          )}

          <QuestionGroupContent $marginLeft={15}>
            {questionGroup.questions &&
              questionGroup.questions
                .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
                .map((questionObj, index) => (
                  <Question
                    question={questionObj}
                    watch={watch}
                    groupVisibleConditions={questionGroup.visibleConditions}
                    preFilledQuestions={preFilledQuestions}
                    control={control}
                    isSelectScoringTooltip={isSelectScoringTooltip}
                    {...props}
                    key={index}
                  />
                ))}
          </QuestionGroupContent>
        </QuestionGroupWrapper>
      );
    } else {
      return (
        <Multiple
          questionGroup={questionGroup}
          preFilledQuestions={preFilledQuestions}
          watch={watch}
          control={control}
          {...props}
        />
      );
    }
  }

  return null;
};

const Multiple = ({ questionGroup, watch, control, preFilledQuestions, ...props }) => {
  const { fields, append, remove } = useFieldArray({
    control: control,
    name: `group_${questionGroup.id}`
  });

  const appendValues = useMemo(() => {
    const result = {};

    questionGroup.questions.forEach((item) => (result[item.id] = ''));

    return result;
  }, [questionGroup]);

  useEffect(() => {
    const multipleMin = questionGroup.multipleMin || 1;

    if (append && fields.length < multipleMin) {
      append(new Array(multipleMin).fill({ ...appendValues }));
    }
  }, [questionGroup, appendValues, append, fields]);

  const multipleMin = useMemo(() => {
    return questionGroup?.multipleMin || 1;
  }, [questionGroup]);

  return (
    <Container $marginTop={15}>
      {questionGroup.title && (
        <Container $marginBottom={20}>
          <Text $fontSize={17} $fontWeight={600}>
            {createLinkFromText(questionGroup.title)}
          </Text>
        </Container>
      )}

      {fields.map((field, fieldIndex) => {
        const groupPreFilledObj = preFilledQuestions[`group_${questionGroup.id}.${fieldIndex}`];

        return (
          <MultipleBox key={field.id} $lastBox={fields.length - 1 === fieldIndex}>
            {questionGroup.questions &&
              questionGroup.questions.map((questionObj, index) => (
                <Container $paddingY={8} key={questionObj.id}>
                  {questionObj.tooltips && index === 0 && fieldIndex === 0 && (
                    <TextTooltip
                      title='Image requirements'
                      textArray={questionObj.tooltips}
                      marginTop={-18}
                      marginBottom={25}
                    />
                  )}
                  <Question
                    question={questionObj}
                    watch={watch}
                    control={control}
                    groupVisibleConditions={questionGroup.visibleConditions}
                    name={`group_${questionGroup.id}.${fieldIndex}.${questionObj.id}`}
                    key={index}
                    preFilledQuestions={preFilledQuestions}
                    disableQuestion={groupPreFilledObj && groupPreFilledObj[questionObj.id]}
                    {...props}
                  />
                </Container>
              ))}

            {fieldIndex !== 0 && (
              <DeleteButtonContainer>
                <TrashIcon
                  onClick={() => remove(fieldIndex)}
                  disabled={groupPreFilledObj || fieldIndex < multipleMin}
                />
              </DeleteButtonContainer>
            )}
          </MultipleBox>
        );
      })}

      {!props.disabled && (
        <Button
          onClick={() => append({ ...appendValues })}
          icon={<PlusOutlined />}
          shape='circle'
          size='x-small'
          block={false}
          disabled={questionGroup.multipleMax && fields.length >= questionGroup.multipleMax}
          style={{ margin: '5px 0 25px' }}
        />
      )}
    </Container>
  );
};

const MultipleBox = styled.div`
  position: relative;
  border-bottom: 1px solid #dde4e9;
  ${(p) => (p.$lastBox ? 'border: none;' : 'padding-bottom: 16px;')}

  &:last-child {
    border-bottom: none;
  }
`;
const QuestionGroupWrapper = styled(Container)`
  padding: 24px 0;
  border-bottom: 1px solid #dde4e9;

  &:last-child {
    border-bottom: unset;
  }
`;
const QuestionGroupContent = styled(Container)`
  > *:not(:last-child) {
    margin-bottom: 14px;
  }
`;
const DeleteButtonContainer = styled.div`
  position: absolute;
  top: -10px;
  right: 0;
  z-index: 2;
`;
